import { supabase } from '../services/auth.service';
import { handleSessionExpired } from './session-guard';

type FunctionInvokeOptions = Parameters<typeof supabase.functions.invoke>[1];

interface EdgeFunctionError {
  message: string;
  code: string;
}

interface EdgeFunctionResult<T = unknown> {
  data: T;
}

export class EdgeFunctionHttpError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'EdgeFunctionHttpError';
    this.code = code;
  }
}

/**
 * Error codes that can never succeed on retry — retrying wastes calls and
 * delays user-visible failure. Deterministic failures (auth, validation,
 * decrypt, not-found, conflicts) belong here.
 */
const NON_RETRYABLE_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED',
  'SESSION_EXPIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'METHOD_NOT_ALLOWED',
  'VALIDATION_ERROR',
  'CONFLICT',
  'BAD_REQUEST',
  'DECRYPTION_FAILED',
  'CREDENTIALS_INVALID',
]);

/** True if the error should NOT be retried by react-query. */
export function isNonRetryableError(error: unknown): boolean {
  return error instanceof EdgeFunctionHttpError && NON_RETRYABLE_CODES.has(error.code);
}

export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options?: FunctionInvokeOptions
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, options);

  if (error) {
    // Centralized SESSION_EXPIRED handling (forced logout + redirect).
    await handleSessionExpired(error);
    throw error;
  }

  if (!data) {
    throw new EdgeFunctionHttpError(`${functionName} returned no data`, 'NO_DATA');
  }

  if (data && typeof data === 'object' && 'error' in data) {
    const fnError = (data as { error?: EdgeFunctionError }).error;
    if (fnError) {
      // SESSION_EXPIRED may arrive here on HTTP 200 responses. Force logout
      // before surfacing the error to callers.
      await handleSessionExpired(data);
      throw new EdgeFunctionHttpError(
        fnError.message || `${functionName} failed`,
        fnError.code || 'UNKNOWN'
      );
    }
  }

  return data as T;
}
